/**
 * Shop interaction flow (HEX-39 origin; card tab HEX-63; economy rework HEX-72).
 *
 * The town shop is now a **persistent, depleting card source** (ADR-0015 §6,
 * shop-refresh open-Q resolved in HEX-72):
 *   - Stock is rolled ONCE per shop and persisted on its tile lifecycle blob
 *     (`ShopLifecycleState.cardStock`). It does **not** refresh per visit —
 *     buying a card removes it permanently, so the next character (or the same
 *     one on a later life) sees the depleted shelf.
 *   - Buying the **last** card auto-rolls a fresh shelf (the shop is never
 *     permanently empty).
 *   - A player may pay `SHOP_RESTOCK_PRICE` gold to force a fresh shelf anytime.
 *   - Dice (`die` fuel) are buyable — they ride the shop pool (HEX-72).
 *
 * Healing moved to the town tile (HEX-72 / `TownFlow`); the shop sells cards
 * only. A bought card first-fits into the loadout — the only card store
 * (ADR-0022) — with a minted instance id; the buy is refused if it fits nowhere.
 *
 * An **Inventory** button opens the out-of-combat `InventoryEditor` on the
 * character's persisted inventory so a freshly-bought card can be rearranged in
 * the loadout before leaving. Gold / inventory mutations persist on close;
 * stock mutations persist to the tile immediately on each buy / restock.
 */

import { createRng } from '../combat/dice';
import {
  CARD_PRICES,
  SHOP_RESTOCK_PRICE,
  cardPrice,
  rollShopStock,
  shopStockSeed,
  stockFromIds,
  stockIds,
} from '../combat/shopStock';
import type { Card } from '../combat/types';
import { InventoryEditor } from '../combat/ui/InventoryEditor';
import { type AcquisitionSession, openAcquisition } from '../combat/ui/curationDrawer';
import { canAddCard } from '../combat/ui/inventoryEdit';
import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import type { Persistence } from '../persistence/Persistence';
import { createGlyphElement } from '../render/glyphs';
import type { MapOffer } from './mapItem';
import { buildStallShell, buildWareTile, goldSpan, labelSpan } from './stallChrome';

import '../combat/ui/inventory-editor-styles.css';
import './interaction-modal-styles.css';

/** Re-export so callers/tests have a single import surface for shop pricing. */
export { CARD_PRICES, SHOP_RESTOCK_PRICE };

export interface ShopFlowDeps {
  readonly persistence: Pick<Persistence, 'saveCharacter' | 'saveTileMutations'>;
}

export interface ShopFlowStartOptions {
  readonly character: Character;
  readonly tile: Tile;
  readonly deps: ShopFlowDeps;
  readonly onResolved: () => void | Promise<void>;
  /** Test seam: inject a deterministic initial shelf instead of rolling one.
   * Seeded onto the tile on open so depletion still persists the real path. */
  readonly cardStock?: readonly Card[];
  /**
   * Buyable map (HEX-73) — reveals tiles other travellers discovered around the
   * shop (reveal-not-credit). Built by the game session (it owns the renderer +
   * world store); absent in headless/test contexts that don't wire the world,
   * in which case no map row is shown.
   */
  readonly mapOffer?: MapOffer;
}

export class ShopFlow {
  private modal: HTMLDivElement | null = null;
  /** The wares counter host (`data-role="shop-body"`). */
  private body: HTMLDivElement | null = null;
  /** The footer shelf — holds the map curiosity + actions. */
  private shelf: HTMLDivElement | null = null;
  private goldLine: HTMLSpanElement | null = null;
  private stock: Card[] = [];
  private mapOffer: MapOffer | null = null;
  /** Set once the map has been bought this visit — the row then greys out. */
  private mapBought = false;
  private editor: InventoryEditor | null = null;
  private acquiring: AcquisitionSession | null = null;
  private active = false;

  constructor(private readonly container: HTMLElement) {}

  /**
   * Mount the shop modal if the tile is a shop. Returns true when mounted;
   * false otherwise. Loads (or lazily rolls + persists) the persistent shelf.
   */
  start(opts: ShopFlowStartOptions): boolean {
    if (this.active) return false;
    if (opts.tile.type !== 'shop') return false;
    this.active = true;
    this.mapOffer = opts.mapOffer ?? null;
    this.mapBought = false;
    this.stock = this.loadStock(opts);
    this.modal = this.mountModal(opts);
    this.renderBody(opts);
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.editor?.destroy();
    this.editor = null;
    this.acquiring?.destroy();
    this.acquiring = null;
    this.modal?.remove();
    this.modal = null;
    this.body = null;
    this.shelf = null;
    this.goldLine = null;
    this.mapOffer = null;
    this.mapBought = false;
    this.active = false;
  }

  // ── persistent stock ───────────────────────────────────────────────────

  /** Resolve the shelf at open time: the test seam, else the tile's persisted
   * stock, else a fresh roll. A seam or a fresh roll is persisted to the tile so
   * the durable shelf exists from this point (the pre-v8 read-time default). */
  private loadStock(opts: ShopFlowStartOptions): Card[] {
    const state = opts.tile.lifecycleState;
    const persisted = state !== null && state.kind === 'shop' ? state.cardStock : null;

    if (opts.cardStock) {
      const seeded = [...opts.cardStock];
      this.persistStock(opts, seeded);
      return seeded;
    }
    if (persisted !== null && persisted !== undefined) {
      return stockFromIds(persisted);
    }
    const rolled = this.rollStock(opts);
    this.persistStock(opts, rolled);
    return rolled;
  }

  /** Roll a fresh shelf seeded off the shop's coords (HEX-72) so it is
   * stable per shop across reloads but differs between shops. */
  private rollStock(opts: ShopFlowStartOptions): Card[] {
    const seed = shopStockSeed(opts.tile.q, opts.tile.r);
    return rollShopStock(createRng(seed));
  }

  /** Write the current shelf onto the tile lifecycle blob + flush. No-op if the
   * tile somehow lacks a shop lifecycle state (defensive). */
  private persistStock(opts: ShopFlowStartOptions, stock: readonly Card[]): void {
    const state = opts.tile.lifecycleState;
    if (state === null || state.kind !== 'shop') return;
    opts.tile.lifecycleState = { ...state, cardStock: stockIds(stock) };
    void opts.deps.persistence.saveTileMutations(opts.tile);
  }

  private restock(opts: ShopFlowStartOptions): void {
    this.stock = this.rollStock(opts);
    this.persistStock(opts, this.stock);
  }

  // ── chrome ─────────────────────────────────────────────────────────────

  private mountModal(opts: ShopFlowStartOptions): HTMLDivElement {
    const shell = buildStallShell({
      modalRole: 'shop-modal',
      ariaLabel: 'Shop',
      signTitle: "The Wayfarer's Stall",
      signSubtitle: 'TOWN SHOP · PERMANENT',
      goldRole: 'shop-gold',
      greeting: '“The road ahead is unkind, traveller. Arm yourself before the dark.”',
      waresRole: 'shop-body',
    });
    this.body = shell.wares;
    this.shelf = shell.shelf;
    this.goldLine = shell.goldValue;

    const restockBtn = document.createElement('button');
    restockBtn.type = 'button';
    restockBtn.dataset.action = 'shop-restock';
    restockBtn.className = 'btn hex6-stall__action';
    restockBtn.append(
      createGlyphElement('recursive', { size: 14, colour: 'var(--bone-mid)' }),
      labelSpan('Restock'),
      goldSpan(`${SHOP_RESTOCK_PRICE}g`),
    );
    restockBtn.addEventListener('click', () => this.forceRestock(opts));

    const inventoryBtn = document.createElement('button');
    inventoryBtn.type = 'button';
    inventoryBtn.dataset.action = 'shop-inventory';
    inventoryBtn.className = 'btn hex6-stall__action';
    inventoryBtn.append(
      createGlyphElement('chest', { size: 14, colour: 'var(--bone-mid)' }),
      labelSpan('Inventory'),
    );
    inventoryBtn.addEventListener('click', () => this.openInventory(opts));

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.dataset.action = 'shop-close';
    closeBtn.className = 'btn ghost hex6-stall__action';
    closeBtn.textContent = 'Leave';
    closeBtn.addEventListener('click', () => {
      void this.close(opts);
    });

    shell.actions.append(restockBtn, inventoryBtn, closeBtn);

    this.container.appendChild(shell.root);
    return shell.root;
  }

  // ── body rendering ─────────────────────────────────────────────────────

  private renderBody(opts: ShopFlowStartOptions): void {
    if (!this.body) return;
    this.renderCards(opts);
    this.renderMapOffer(opts);
    this.refreshGold(opts.character);
    this.refreshRestock(opts.character);
  }

  /**
   * Map curiosity (HEX-73) on the footer shelf, left of the actions. Shows how
   * many tiles the purchase reveals so 100g never buys a 1-tile map; disabled
   * when nothing new is revealable, the player can't afford it, or it's already
   * been bought this visit. No-op when the host wired no offer (headless/test).
   */
  private renderMapOffer(opts: ShopFlowStartOptions): void {
    if (!this.shelf || !this.mapOffer) return;
    // Drop any prior render before re-adding (renderBody runs after each buy).
    this.shelf.querySelector('[data-role="shop-map-offer"]')?.remove();
    const offer = this.mapOffer;
    const character = opts.character;
    const count = offer.revealCount;

    const row = document.createElement('div');
    row.className = 'hex6-stall__curio';
    row.dataset.role = 'shop-map-offer';

    const mark = document.createElement('div');
    mark.className = 'hex6-stall__curio-mark';
    mark.appendChild(createGlyphElement('eye', { size: 22, colour: 'var(--gold)' }));

    const text = document.createElement('div');
    text.className = 'hex6-stall__curio-text';
    const heading = document.createElement('div');
    heading.className = 'hex6-stall__curio-name';
    heading.textContent = "Cartographer's Chart";
    const label = document.createElement('div');
    label.className = 'hex6-stall__curio-desc';
    label.dataset.role = 'shop-map-label';
    label.textContent = this.mapBought
      ? 'Map — revealed'
      : `Map — reveals ${count} nearby tile${count === 1 ? '' : 's'}`;
    text.append(heading, label);

    const plate = document.createElement('div');
    plate.className = 'hex6-stall__plate';
    const priceChip = document.createElement('span');
    priceChip.className = 'hex6-stall__price';
    const priceCoin = document.createElement('span');
    priceCoin.className = 'hex6-stall__coin';
    const price = document.createElement('span');
    price.dataset.role = 'shop-map-price';
    price.textContent = `${offer.price}g`;
    priceChip.append(priceCoin, price);

    const buy = document.createElement('button');
    buy.type = 'button';
    buy.dataset.action = 'shop-buy-map';
    buy.className = 'btn primary hex6-stall__buy';
    buy.textContent = 'Buy';
    const affordable = character.gold >= offer.price;
    const hasReveal = count > 0;
    buy.disabled = this.mapBought || !affordable || !hasReveal;
    buy.title = this.mapBought
      ? 'Already revealed'
      : !hasReveal
        ? 'Nothing new to reveal nearby'
        : !affordable
          ? 'Not enough gold'
          : `Reveals ${count} tile${count === 1 ? '' : 's'} other travellers discovered`;
    buy.addEventListener('click', () => void this.buyMap(opts));

    plate.append(priceChip, buy);
    row.append(mark, text, plate);
    this.shelf.prepend(row);
  }

  private async buyMap(opts: ShopFlowStartOptions): Promise<void> {
    const offer = this.mapOffer;
    if (this.mapBought || offer === null) return;
    if (offer.revealCount <= 0 || opts.character.gold < offer.price) return;
    // The offer owns the whole transaction (gold + reveal + persist) — it holds
    // the renderer + world store this flow has no handle on.
    await offer.buy();
    this.mapBought = true;
    this.renderBody(opts);
  }

  private renderCards(opts: ShopFlowStartOptions): void {
    if (!this.body) return;
    const character = opts.character;

    if (this.stock.length === 0) {
      const sold = document.createElement('div');
      sold.className = 'hex6-stall__empty';
      sold.dataset.role = 'shop-sold-out';
      sold.textContent = 'The counter is bare — restock to refresh the shelf.';
      this.body.replaceChildren(sold);
      return;
    }

    const tiles = this.stock.map((card) =>
      buildWareTile({
        card,
        price: cardPrice(card),
        offerRole: 'shop-card-offer',
        priceRole: 'shop-card-price',
        buyAction: 'shop-buy-card',
        affordable: character.gold >= cardPrice(card),
        fits: canAddCard(character.inventory, card),
        onBuy: () => this.buyCard(opts, card),
      }),
    );

    this.body.replaceChildren(...tiles);
  }

  private buyCard(opts: ShopFlowStartOptions, card: Card): void {
    const character = opts.character;
    const price = cardPrice(card);
    if (character.gold < price || !this.modal) return;
    // Present the bought card beside the loadout (HEX-149): place it (curating to
    // make room), or back out. The shop is only charged on a place — a back-out
    // forgoes the purchase, gold untouched.
    this.modal.style.display = 'none';
    const session = openAcquisition(this.container, character, {
      item: card,
      declineLabel: 'Leave it',
    });
    this.acquiring = session;
    void session.done.then((outcome) => {
      this.acquiring = null;
      if (this.modal) this.modal.style.display = '';
      if (outcome === 'placed') {
        character.gold -= price;
        // Remove this exact offered instance from the shelf — permanently (HEX-72).
        const idx = this.stock.indexOf(card);
        if (idx >= 0) this.stock.splice(idx, 1);
        // Buying the last card auto-rolls a fresh shelf so the shop is never stuck
        // empty; otherwise persist the now-depleted shelf.
        if (this.stock.length === 0) this.restock(opts);
        else this.persistStock(opts, this.stock);
      }
      // Persist regardless — any curation destructions (run-scoped) must stick.
      void opts.deps.persistence.saveCharacter(character);
      this.renderBody(opts);
    });
  }

  private forceRestock(opts: ShopFlowStartOptions): void {
    const character = opts.character;
    if (character.gold < SHOP_RESTOCK_PRICE) return;
    character.gold -= SHOP_RESTOCK_PRICE;
    this.restock(opts);
    void opts.deps.persistence.saveCharacter(character);
    this.renderBody(opts);
  }

  // ── inventory editor ───────────────────────────────────────────────────

  private openInventory(opts: ShopFlowStartOptions): void {
    if (this.editor || !this.modal) return;
    // Hide the shop panel while the editor is up; restore on close.
    this.modal.style.display = 'none';
    const editor = new InventoryEditor(opts.character.inventory);
    this.editor = editor;
    editor.mount(this.container, {
      inventory: opts.character.inventory,
      onChange: (inv) => {
        opts.character.inventory = inv;
      },
      onClose: () => {
        editor.destroy();
        this.editor = null;
        if (this.modal) this.modal.style.display = '';
        this.renderBody(opts);
      },
    });
  }

  // ── helpers ────────────────────────────────────────────────────────────

  private refreshGold(character: Character): void {
    if (this.goldLine) this.goldLine.textContent = String(character.gold);
  }

  private refreshRestock(character: Character): void {
    const btn = this.modal?.querySelector<HTMLButtonElement>('[data-action="shop-restock"]');
    if (btn) btn.disabled = character.gold < SHOP_RESTOCK_PRICE;
  }

  private async close(opts: ShopFlowStartOptions): Promise<void> {
    if (!this.active) return;
    this.destroy();
    await opts.deps.persistence.saveCharacter(opts.character);
    await opts.onResolved();
  }
}
